import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Pagination from '../../../components/common/Pagination';
import { db } from '../../../firebase/config';
import { formatUserName } from '../../../utils/userName';
import {
  collection, getDocs, doc, getDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';

const STATUS_META = {
  requested: { label: '신청', color: '#3B82F6' },
  approved: { label: '승인', color: '#F59E0B' },
  completed: { label: '완료', color: '#22C55E' },
  rejected: { label: '거절', color: '#EF4444' },
};

const STATUS_ORDER = { requested: 0, approved: 1, completed: 2, rejected: 3 };

const PER_PAGE = 20;

const WithdrawalManage = () => {
  const { userData } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [receivables, setReceivables] = useState({ total: 0, count: 0, monthly: 0, perPartner: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // orderBy('createdAt') 가 필드 없는 doc 을 누락시키므로 전체 fetch 후 클라이언트 정렬
      const snap = await getDocs(collection(db, 'withdrawal_requests'));
      const enriched = await Promise.all(
        snap.docs.map(async (d) => {
          const w = d.data();
          let realtorName = '-';
          let applicantName = '-';
          if (w.realtorId || w.realtor_id) {
            try {
              const uid = w.realtorId || w.realtor_id;
              const rSnap = await getDoc(doc(db, 'users', uid));
              if (rSnap.exists()) {
                const rd = rSnap.data();
                realtorName = rd.business_name || '-';
                applicantName = rd.name || '-';
              }
            } catch { /* skip */ }
          }
          const ts = w.createdAt || w.created_at || w.requestedAt || w.requested_at;
          const dt = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (typeof ts === 'string' ? new Date(ts) : null));
          const pts = w.processedAt || w.processed_at;
          const pdt = pts?.toDate ? pts.toDate() : (pts?.seconds ? new Date(pts.seconds * 1000) : (typeof pts === 'string' ? new Date(pts) : null));
          return {
            id: d.id,
            realtorId: w.realtorId || w.realtor_id || '',
            realtorName,
            applicantName,
            amount: w.amount || 0,
            bankName: w.bankName || w.bank || '',
            accountNumber: w.accountNumber || w.account || '',
            status: w.status || 'requested',
            createdAt: dt,
            processedAt: pdt,
          };
        })
      );
      enriched.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
      setData(enriched);

      // 미수 (출금 실적 미지급 등)
      const recvSnap = await getDocs(collection(db, 'partner_receivables'));
      const partnerIds = new Set();
      let totalReceivable = 0;
      let monthlyReceivable = 0;
      const thisMonthStart = new Date();
      thisMonthStart.setDate(1);
      thisMonthStart.setHours(0, 0, 0, 0);

      const perPartner = new Map();
      recvSnap.docs.forEach((d) => {
        const r = d.data();
        const pid = r.partner_id || r.partnerId;
        if (pid && r.status !== 'paid') {
          partnerIds.add(pid);
          totalReceivable += r.amount || 0;
          const rc = r.created_at || r.createdAt;
          const rdt = rc?.toDate ? rc.toDate() : (rc?.seconds ? new Date(rc.seconds * 1000) : null);
          if (rdt && rdt >= thisMonthStart) monthlyReceivable += r.amount || 0;

          const entry = perPartner.get(pid) || { id: pid, amount: 0, partnerName: '-' };
          entry.amount += r.amount || 0;
          perPartner.set(pid, entry);
        }
      });

      for (const [pid, entry] of perPartner.entries()) {
        try {
          const [uSnap, pSnap] = await Promise.all([
            getDoc(doc(db, 'users', pid)),
            getDoc(doc(db, 'partners', pid)),
          ]);
          if (uSnap.exists()) {
            const u = uSnap.data();
            const p = pSnap.exists() ? pSnap.data() : {};
            entry.partnerName = formatUserName({
              ...u,
              business_name: p.business_name || p.businessName || u.business_name || '',
            });
          }
        } catch { /* skip */ }
      }

      setReceivables({
        total: totalReceivable,
        count: partnerIds.size,
        monthly: monthlyReceivable,
        perPartner: Array.from(perPartner.values()).sort((a, b) => b.amount - a.amount),
      });
    } catch (err) {
      console.error('출금 관리 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'withdrawal_requests', id), {
        status: newStatus,
        processedAt: serverTimestamp(),
        processedBy: userData?.uid || '',
      });
      fetchData();
    } catch (err) {
      console.error('상태 변경 실패:', err);
    }
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    const list = data.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (kw && !(`${d.realtorName || ''}${d.applicantName || ''}`).includes(kw)) return false;
      return true;
    });
    const getVal = (d) => {
      if (sortKey === 'applicantName') return (d.applicantName || '').toLowerCase();
      if (sortKey === 'realtorName') return (d.realtorName || '').toLowerCase();
      if (sortKey === 'amount') return d.amount || 0;
      if (sortKey === 'status') return STATUS_ORDER[d.status] ?? 99;
      if (sortKey === 'processedAt') return d.processedAt?.getTime() || 0;
      return d.createdAt?.getTime() || 0;
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, statusFilter, keyword, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'createdAt' || key === 'processedAt' || key === 'amount' ? 'desc' : 'asc'); }
    setPage(1);
  };
  const sortInd = (key) => (
    <SortInd $active={sortKey === key}>{sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));

  const counts = useMemo(() => ({
    requested: data.filter((d) => d.status === 'requested').length,
    approved: data.filter((d) => d.status === 'approved').length,
    completed: data.filter((d) => d.status === 'completed').length,
  }), [data]);

  const handleExport = () => {
    const rows = [
      ['신청자', '부동산', '금액', '은행', '계좌', '상태', '신청일', '처리일'],
      ...filtered.map((d) => [
        d.applicantName,
        d.realtorName,
        d.amount,
        d.bankName,
        d.accountNumber,
        STATUS_META[d.status]?.label || '',
        d.createdAt ? d.createdAt.toISOString().slice(0, 10) : '',
        d.processedAt ? d.processedAt.toISOString().slice(0, 10) : '',
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `withdrawal_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatusText = ({ status }) => {
    const s = STATUS_META[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>;
  };

  const fmtDate = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '-';

  return (
    <Container>
      <TopRow>
        <Title>공인중개사 정산 (수수료 지급)</Title>
        <TopActions>
          <TopBtn onClick={handleExport}>엑셀 다운로드</TopBtn>
          <TopBtn onClick={fetchData}>새로고침</TopBtn>
        </TopActions>
      </TopRow>

      <PolicyBox>
        <PolicyLine>• 출금 신청은 매월 말일 정산분 기준으로 가능하며, 신청 후 관리자 승인 → 완료 처리 단계로 진행됩니다.</PolicyLine>
        <PolicyLine>• 출금 산정 기준: 정산완료된 수수료만 출금 가능하며, 최소 출금 금액 조건과 계좌 인증이 필요합니다.</PolicyLine>
        <PolicyLine>• 세금 산정: 개인은 원천징수 3.3%, 사업자는 부가세 10%가 별도 처리됩니다.</PolicyLine>
        <PolicyLine>• 직급 기준: 개인 계정과 사업자 계정의 지급 한도 및 증빙 서류 기준이 각각 다릅니다.</PolicyLine>
      </PolicyBox>

      <SectionHead>미수 현황</SectionHead>
      <ReceivableRow>
        <ReceivableCard>
          <StatLabel>전체 미수</StatLabel>
          <StatValue $color="#EF4444">{(receivables?.total || 0).toLocaleString()}원</StatValue>
        </ReceivableCard>
        <ReceivableCard>
          <StatLabel>미수 업체 수</StatLabel>
          <StatValue>{receivables?.count || 0}건</StatValue>
        </ReceivableCard>
        <ReceivableCard>
          <StatLabel>업체별 미수 금액</StatLabel>
          <StatValue>{(receivables?.perPartner?.[0]?.amount || 0).toLocaleString()}원</StatValue>
          <StatSub>최대 미수 업체 기준</StatSub>
        </ReceivableCard>
        <ReceivableCard>
          <StatLabel>당월 미수금액</StatLabel>
          <StatValue $color="#F59E0B">{(receivables?.monthly || 0).toLocaleString()}원</StatValue>
        </ReceivableCard>
      </ReceivableRow>

      {receivables?.perPartner?.length > 0 && (
        <PartnerList>
          {receivables.perPartner.slice(0, 8).map((p) => (
            <PartnerItem key={p.id}>
              <span>{p.partnerName}</span>
              <PartnerAmount>{p.amount.toLocaleString()}원</PartnerAmount>
            </PartnerItem>
          ))}
        </PartnerList>
      )}

      <SectionHead style={{ marginTop: 24 }}>출금 신청</SectionHead>
      <SummaryRow>
        <SummaryCard>
          <StatLabel>대기 중</StatLabel>
          <StatValue $color="#3B82F6">{counts.requested}건</StatValue>
        </SummaryCard>
        <SummaryCard>
          <StatLabel>승인됨</StatLabel>
          <StatValue $color="#F59E0B">{counts.approved}건</StatValue>
        </SummaryCard>
        <SummaryCard>
          <StatLabel>출금 완료</StatLabel>
          <StatValue $color="#22C55E">{counts.completed}건</StatValue>
        </SummaryCard>
      </SummaryRow>

      <ToolRow>
        <SearchInput
          placeholder="공인중개사 검색 (부동산명 / 신청자)"
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
        />
        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">상태 전체</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
      </ToolRow>

      {loading ? (
        <EmptyText>로딩 중...</EmptyText>
      ) : paged.length === 0 ? (
        <EmptyText>출금 신청이 없습니다.</EmptyText>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th $sortable $active={sortKey === 'applicantName'} onClick={() => toggleSort('applicantName')}>신청자{sortInd('applicantName')}</Th>
                <Th $sortable $active={sortKey === 'realtorName'} onClick={() => toggleSort('realtorName')}>부동산{sortInd('realtorName')}</Th>
                <Th $sortable $active={sortKey === 'amount'} onClick={() => toggleSort('amount')} style={{ textAlign: 'right' }}>신청금액{sortInd('amount')}</Th>
                <Th>계좌</Th>
                <Th $sortable $active={sortKey === 'createdAt'} onClick={() => toggleSort('createdAt')}>신청일{sortInd('createdAt')}</Th>
                <Th $sortable $active={sortKey === 'status'} onClick={() => toggleSort('status')}>상태{sortInd('status')}</Th>
                <Th $sortable $active={sortKey === 'processedAt'} onClick={() => toggleSort('processedAt')}>처리일{sortInd('processedAt')}</Th>
                <Th>관리</Th>
              </tr>
            </thead>
            <tbody>
              {paged.map((d) => (
                <Tr key={d.id}>
                  <Td>{d.applicantName}</Td>
                  <Td>{d.realtorName}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>{d.amount.toLocaleString()}원</Td>
                  <Td style={{ color: '#6B7280' }}>{d.bankName} {d.accountNumber}</Td>
                  <Td>{fmtDate(d.createdAt)}</Td>
                  <Td><StatusText status={d.status} /></Td>
                  <Td>{fmtDate(d.processedAt)}</Td>
                  <Td>
                    {d.status === 'requested' && (
                      <InlineActions>
                        <InlineBtn onClick={() => handleStatusChange(d.id, 'approved')}>승인</InlineBtn>
                        <InlineBtnDanger onClick={() => handleStatusChange(d.id, 'rejected')}>거절</InlineBtnDanger>
                      </InlineActions>
                    )}
                    {d.status === 'approved' && (
                      <InlineActions>
                        <InlineBtn onClick={() => handleStatusChange(d.id, 'completed')}>완료</InlineBtn>
                      </InlineActions>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <PageFoot>
        <PageInfo>페이지 {page} / {totalPages} · 총 {filtered.length}건</PageInfo>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </PageFoot>
    </Container>
  );
};

export default WithdrawalManage;

// --- Styled ---
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0;`;
const TopActions = styled.div`display:flex;gap:6px;`;
const TopBtn = styled.button`padding:7px 12px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#F9FAFB;}`;

const PolicyBox = styled.div`
  background:#FFFBEB;
  color:#713F12;
  border:1px solid #FDE68A;
  border-radius:${({theme})=>theme.radius.sm};
  padding:12px 16px;
  font-size:13px;
  line-height:1.7;
  margin-bottom:20px;
`;
const PolicyLine = styled.div``;

const SectionHead = styled.h3`font-size:14px;font-weight:600;color:#1F2937;margin:0 0 10px;`;

const ReceivableRow = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:10px;`;
const ReceivableCard = styled.div`background:#fff;padding:14px 16px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const StatLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const StatValue = styled.div`font-size:18px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;
const StatSub = styled.div`font-size:11px;color:${({theme})=>theme.colors.textLight};margin-top:3px;`;

const PartnerList = styled.div`background:#fff;border:1px solid ${({theme})=>theme.colors.borderLight};border-radius:${({theme})=>theme.radius.md};padding:10px 16px;`;
const PartnerItem = styled.div`display:flex;justify-content:space-between;align-items:center;padding:8px 0;font-size:13px;color:#1F2937;&:not(:last-child){border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const PartnerAmount = styled.span`font-weight:600;color:#EF4444;`;

const SummaryRow = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:14px;`;
const SummaryCard = styled.div`background:#fff;padding:14px 16px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;

const ToolRow = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:12px;`;
const SearchInput = styled.input`flex:1;padding:8px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;background:#fff;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;

const TableWrapper = styled.div`overflow-x:auto;background:#fff;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  background:#F9FAFB;
  padding:12px 14px;
  text-align:left;
  font-size:12px;
  font-weight:500;
  color:${({theme,$active})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:1px solid ${({theme})=>theme.colors.border};
  white-space:nowrap;
  ${({$sortable,theme})=>$sortable&&`
    cursor:pointer;
    user-select:none;
    &:hover { color: ${theme.colors.primary}; }
  `}
`;
const SortInd = styled.span`
  display:inline-block;
  margin-left:6px;
  color:${({theme,$active})=>$active?theme.colors.primary:'#C0C7D1'};
  font-size:10px;
  line-height:1;
  vertical-align:middle;
`;
const Tr = styled.tr`&:not(:last-child) td{border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;

const InlineActions = styled.div`display:flex;gap:6px;`;
const InlineBtn = styled.button`padding:4px 10px;border:1px solid ${({theme})=>theme.colors.success};background:#fff;color:${({theme})=>theme.colors.success};border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#F0FDF4;}`;
const InlineBtnDanger = styled.button`padding:4px 10px;border:1px solid ${({theme})=>theme.colors.danger};background:#fff;color:${({theme})=>theme.colors.danger};border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#FEF2F2;}`;

const EmptyText = styled.p`text-align:center;padding:56px 0;font-size:14px;color:${({theme})=>theme.colors.textSecondary};background:#fff;border-radius:${({theme})=>theme.radius.md};`;
const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
